import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, FormHelperText } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Select from 'react-select';
import InputGroupV2 from '../InputGroupV2';
import { getReactSelectStyles } from '../reactSelectTheme';
import { useReactSelectTabBlur } from '../reactSelectTabSelect';
import {
  getCachedMunicipioOptions,
  resolveMunicipioOptionsByCodigoIbge,
  searchMunicipioOptions,
  warmMunicipioCache,
} from './municipioOptionsCache';
import {
  getMunicipioNoOptionsMessage,
  MIN_MUNICIPIO_QUERY_LENGTH,
} from './municipioSelectSearch';
import { normalizeText } from '../selectSearchFilter';

const useMunicipioOptions = () => {
  const [options, setOptions] = useState(() => getCachedMunicipioOptions());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;

    const syncOptions = () => {
      if (!active) return;
      setOptions(getCachedMunicipioOptions());
    };

    syncOptions();

    if (getCachedMunicipioOptions().length === 0) {
      setLoading(true);
      warmMunicipioCache()
        .then(() => {
          if (!active) return;
          syncOptions();
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    }

    return () => {
      active = false;
    };
  }, []);

  return { options, setOptions, loading };
};

const buildDisplayLabel = (nome, uf) => {
  if (!nome) return '';
  return uf ? `${nome} / ${uf}` : nome;
};

const toOptionValue = (codigoIbge) => (
  codigoIbge == null || codigoIbge === '' ? null : String(codigoIbge)
);

const buildOptionFromCodigo = (codigoIbge, { nome, uf, pending = false } = {}) => {
  const normalized = String(codigoIbge);
  const label = nome
    ? buildDisplayLabel(nome, uf)
    : (pending ? '…' : normalized);

  return {
    value: normalized,
    label,
    codigoIbge: normalized,
    nome: nome ?? null,
    uf: uf ?? null,
    municipio: {
      codigoIbge: normalized,
      nomeOriginal: nome ?? null,
      uf: uf ?? null,
    },
  };
};

const MunicipioSelectV2 = ({
  onSelect,
  value,
  nome,
  uf,
  defaultCodigoIbge,
  error,
  helperText,
  disabled,
  multiSelect = false,
  label = 'Cidades',
  isRequired,
  placeholder = 'Digite para buscar município...',
}) => {
  const theme = useTheme();
  const { selectRef, tabSelectsValue, onKeyDown } = useReactSelectTabBlur();
  const { options: allOptions, setOptions, loading: cacheLoading } = useMunicipioOptions();
  const [inputValue, setInputValue] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [resolveLoading, setResolveLoading] = useState(false);
  const [knownOptions, setKnownOptions] = useState(() => new Map());

  const mergedOptions = useMemo(() => {
    const merged = new Map(allOptions.map((option) => [String(option.value), option]));
    knownOptions.forEach((option, key) => {
      if (!merged.has(key)) {
        merged.set(key, option);
      }
    });
    return Array.from(merged.values());
  }, [allOptions, knownOptions]);

  const optionsByValue = useMemo(
    () => new Map(mergedOptions.map((option) => [String(option.value), option])),
    [mergedOptions]
  );

  const labelHintsByCodigo = useMemo(() => {
    const hints = new Map();

    if (!multiSelect || !nome) {
      return hints;
    }

    const codes = Array.isArray(value) ? value.map(String) : [];
    if (codes.length === 1 && nome) {
      hints.set(codes[0], buildOptionFromCodigo(codes[0], { nome, uf }));
    }

    if (defaultCodigoIbge && nome) {
      hints.set(String(defaultCodigoIbge), buildOptionFromCodigo(defaultCodigoIbge, { nome, uf }));
    }

    return hints;
  }, [multiSelect, value, nome, uf, defaultCodigoIbge]);

  const selectedValue = useMemo(() => {
    if (multiSelect) {
      if (!Array.isArray(value) || value.length === 0) {
        return [];
      }

      return value.map((codigoIbge) => {
        const normalized = String(codigoIbge);
        const found = optionsByValue.get(normalized) || labelHintsByCodigo.get(normalized);
        if (found) {
          return found;
        }

        return buildOptionFromCodigo(normalized, { pending: resolveLoading });
      });
    }

    const normalizedValue = toOptionValue(value);
    if (!normalizedValue) {
      return null;
    }

    const found = optionsByValue.get(normalizedValue);
    if (found) {
      return found;
    }

    if (nome) {
      return {
        value: normalizedValue,
        label: buildDisplayLabel(nome, uf),
        municipio: {
          codigoIbge: normalizedValue,
          nomeOriginal: nome,
          uf: uf ?? null,
        },
      };
    }

    return { value: normalizedValue, label: normalizedValue };
  }, [multiSelect, value, optionsByValue, labelHintsByCodigo, nome, uf, resolveLoading]);

  const selectStyles = useMemo(
    () => getReactSelectStyles(theme, { menuZIndex: 99999, error }),
    [theme, error]
  );

  const valueKey = multiSelect && Array.isArray(value) ? value.join(',') : String(value ?? '');

  useEffect(() => {
    if (!multiSelect || !Array.isArray(value) || value.length === 0) {
      return undefined;
    }

    const missing = value
      .map(String)
      .filter((code) => {
        if (optionsByValue.get(code)?.nome) {
          return false;
        }
        if (labelHintsByCodigo.get(code)?.nome) {
          return false;
        }
        return true;
      });

    if (!missing.length) {
      return undefined;
    }

    let active = true;
    setResolveLoading(true);

    resolveMunicipioOptionsByCodigoIbge(missing)
      .then(() => {
        if (!active) return;
        setOptions(getCachedMunicipioOptions());
      })
      .finally(() => {
        if (active) setResolveLoading(false);
      });

    return () => {
      active = false;
    };
  }, [multiSelect, valueKey, value, optionsByValue, labelHintsByCodigo, setOptions]);

  const isSearching = normalizeText(inputValue.trim()).length >= MIN_MUNICIPIO_QUERY_LENGTH;
  const loading = cacheLoading || searchLoading || resolveLoading;

  useEffect(() => {
    if (!isSearching) {
      setSearchResults([]);
      setSearchLoading(false);
      return undefined;
    }

    let active = true;
    setSearchLoading(true);

    searchMunicipioOptions(inputValue)
      .then((results) => {
        if (!active) return;
        setSearchResults(results);
        setOptions(getCachedMunicipioOptions());
      })
      .finally(() => {
        if (active) setSearchLoading(false);
      });

    return () => {
      active = false;
    };
  }, [inputValue, isSearching, setOptions]);

  const menuOptions = useMemo(() => {
    if (isSearching) {
      return searchResults;
    }

    if (multiSelect) {
      return selectedValue || [];
    }

    return selectedValue ? [selectedValue] : [];
  }, [isSearching, searchResults, multiSelect, selectedValue]);

  const displayedValue = useMemo(() => {
    if (multiSelect) {
      return selectedValue;
    }

    if (isSearching) {
      return null;
    }

    return selectedValue;
  }, [multiSelect, selectedValue, isSearching]);

  const DropdownIndicator = () => (
    <ArrowDropDownIcon sx={{ color: 'text.secondary' }} />
  );
  const IndicatorSeparator = () => '';

  const onInputChangeLocal = (nextValue, { action }) => {
    if (action === 'input-blur' || action === 'menu-close') {
      return;
    }

    setInputValue(nextValue);
  };

  const onMenuOpenLocal = () => {
    setInputValue('');
  };

  const onMenuCloseLocal = () => {
    setInputValue('');
    setSearchResults([]);
  };

  const onChangeLocal = (data) => {
    if (multiSelect) {
      if (data?.length) {
        setKnownOptions((prev) => {
          const next = new Map(prev);
          data.forEach((option) => {
            if (option?.value) {
              next.set(String(option.value), option);
            }
          });
          return next;
        });
      }

      onSelect(data ? data.map((option) => option.value) : null);
      return;
    }

    onSelect(data ? data.municipio : null);
    setInputValue('');
    setSearchResults([]);
  };

  return (
    <>
      <InputGroupV2 label={label} isRequired={isRequired}>
        <Select
          ref={selectRef}
          components={{ DropdownIndicator, IndicatorSeparator }}
          styles={selectStyles}
          options={menuOptions}
          value={displayedValue}
          onChange={onChangeLocal}
          onInputChange={onInputChangeLocal}
          onMenuOpen={onMenuOpenLocal}
          onMenuClose={onMenuCloseLocal}
          inputValue={inputValue}
          onKeyDown={onKeyDown}
          tabSelectsValue={tabSelectsValue}
          placeholder={placeholder}
          isLoading={loading}
          loadingMessage={() => 'Buscando municípios...'}
          noOptionsMessage={({ inputValue: query }) =>
            getMunicipioNoOptionsMessage(query)}
          isMulti={multiSelect}
          isClearable
          isDisabled={disabled}
          isSearchable
          filterOption={() => true}
          menuPortalTarget={document.body}
        />
        {error && (
          <Box
            component="p"
            sx={{
              m: 0,
              mt: 0.5,
              color: 'error.main',
              fontSize: 11,
              fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
              fontWeight: 400,
              letterSpacing: '0.33px',
              lineHeight: 1,
              minHeight: '1em',
              textAlign: 'left',
            }}
          >
            {helperText || 'O campo é obrigatório'}
          </Box>
        )}
      </InputGroupV2>
      {error && helperText && multiSelect && (
        <FormHelperText error sx={{ mx: 1.5, mt: 0.5 }}>
          {helperText}
        </FormHelperText>
      )}
    </>
  );
};

MunicipioSelectV2.propTypes = {
  onSelect: PropTypes.func.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.string,
  ]),
  nome: PropTypes.string,
  uf: PropTypes.string,
  defaultCodigoIbge: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  error: PropTypes.bool,
  helperText: PropTypes.string,
  disabled: PropTypes.bool,
  multiSelect: PropTypes.bool,
  label: PropTypes.string,
  isRequired: PropTypes.bool,
  placeholder: PropTypes.string,
};

MunicipioSelectV2.defaultProps = {
  value: null,
  nome: undefined,
  uf: undefined,
  defaultCodigoIbge: undefined,
  error: false,
  helperText: null,
  disabled: false,
  multiSelect: false,
  label: 'Cidades',
  isRequired: false,
  placeholder: 'Digite para buscar município...',
};

export default MunicipioSelectV2;
